import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clamp, fmt, pastels, abbr } from '@/util';

const colorFor = (i) => pastels[i % pastels.length];
const CLICK_TOL = 3;
const SNAP = 0.1;
const MIN_SPAN = 0.2;
const GAP_PX = 1;
const HANDLE_W = Math.max(8, GAP_PX + 4);
const SELECT_PAD = 2;
const EPS = 0.05;
const MAX_TICKS = 10;
const THUMB_SIZE = 32;

const safeInside = (start, end) => {
  const pad = Math.max(0.05, SNAP);
  let t = start + pad;

  if (!Number.isFinite(t) || t >= end) {
    t = (start + end) / 2;
  }

  return clamp(t, start + 1e-3, Math.max(start + 1e-3, end - 1e-3));
};

export function SubtitleTimeline({
  durationSec,
  subtitles,
  currentTime = 0,
  onSeek,
  onUpdateSubtitle,
  onUpdateMany,
  activeSubtitleIndex,
  setActiveSubtitleIndex,
  isPlaying = false,
  readOnly = false,
  className = '',
}) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);

  const [thumbLeft, setThumbLeft] = useState(0);
  const [drag, setDrag] = useState(null);

  const syncFromScroll = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    const ratio = maxScroll > 0 ? viewport.scrollLeft / maxScroll : 0;
    const maxLeft = Math.max(0, track.clientWidth - THUMB_SIZE);

    setThumbLeft(ratio * maxLeft);
  }, []);

  const onThumbDrag = useCallback(
    (e) => {
      const track = trackRef.current;
      const viewport = viewportRef.current;
      if (!track || !viewport) return;

      const rect = track.getBoundingClientRect();

      let pos = e.clientX - rect.left - THUMB_SIZE / 2;
      pos = Math.max(0, Math.min(pos, rect.width - THUMB_SIZE));

      const ratio =
        rect.width > THUMB_SIZE ? pos / (rect.width - THUMB_SIZE) : 0;

      viewport.scrollLeft =
        ratio * Math.max(0, viewport.scrollWidth - viewport.clientWidth);

      syncFromScroll();
    },
    [syncFromScroll]
  );

  const stopThumbDrag = useCallback(() => {
    document.removeEventListener('mousemove', onThumbDrag);
    document.removeEventListener('mouseup', stopThumbDrag);
  }, [onThumbDrag]);

  const startThumbDrag = useCallback(
    (e) => {
      e.preventDefault();

      document.addEventListener('mousemove', onThumbDrag);
      document.addEventListener('mouseup', stopThumbDrag);
    },
    [onThumbDrag, stopThumbDrag]
  );

  useEffect(() => {
    return () => {
      stopThumbDrag();
    };
  }, [stopThumbDrag]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    viewport.addEventListener('scroll', syncFromScroll);
    window.addEventListener('resize', syncFromScroll);

    syncFromScroll();

    return () => {
      viewport.removeEventListener('scroll', syncFromScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, [syncFromScroll, subtitles?.length]);

  useEffect(() => {
    if (!isPlaying || drag) return;

    const viewport = viewportRef.current;
    const container = viewport?.querySelector(
      '[data-subtitle-timeline-container]'
    );

    if (!viewport || !container) return;

    const safeDur = durationSec > 0 ? durationSec : 1;
    const ratio = clamp(currentTime, 0, safeDur) / safeDur;
    const playheadX = ratio * container.scrollWidth;

    const left = viewport.scrollLeft;
    const right = left + viewport.clientWidth;
    const padding = 40;

    if (playheadX < left + padding || playheadX > right - padding) {
      viewport.scrollTo({
        left: playheadX - viewport.clientWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [currentTime, durationSec, isPlaying, drag]);

  useEffect(() => {
    if (activeSubtitleIndex == null || drag) return;

    const viewport = viewportRef.current;
    const container = viewport?.querySelector(
      '[data-subtitle-timeline-container]'
    );

    if (!viewport || !container) return;

    const cue = subtitles?.[activeSubtitleIndex - 1];
    if (!cue) return;

    const safeDur = durationSec > 0 ? durationSec : 1;
    const startSec = clamp((cue.start_ms || 0) / 1000, 0, safeDur);
    const targetX = (startSec / safeDur) * container.scrollWidth;

    viewport.scrollTo({
      left: targetX - viewport.clientWidth / 2,
      behavior: 'smooth',
    });
  }, [activeSubtitleIndex, drag, subtitles, durationSec]);

  return (
    <div
      className={`relative w-full select-none overflow-hidden rounded-lg bg-grey-01 pt-4 ${className}`.trim()}
    >
      <div
        ref={trackRef}
        className="relative mb-4 mt-2 h-[1px] rounded bg-gray-300"
      >
        <div
          className="absolute top-1/2 flex -translate-y-1/2 cursor-grab flex-row items-center rounded bg-yellow-400 shadow active:cursor-grabbing"
          style={{
            width: `${THUMB_SIZE}px`,
            height: `${THUMB_SIZE}px`,
            transform: `translate(${thumbLeft}px, -50%)`,
          }}
          onMouseDown={startThumbDrag}
        >
          <span className="mx-auto text-[10px] font-bold text-black">↔</span>
        </div>
      </div>

      <div
        ref={viewportRef}
        className="subtitle-timeline-scrollbarless overflow-x-scroll"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: '16px 0 18px',
        }}
      >
        <style>{`
          .subtitle-timeline-scrollbarless::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        <SubtitleTimelineContent
          durationSec={durationSec}
          subtitles={subtitles}
          currentTime={currentTime}
          onSeek={onSeek}
          onUpdateSubtitle={onUpdateSubtitle}
          onUpdateMany={onUpdateMany}
          activeSubtitleIndex={activeSubtitleIndex}
          setActiveSubtitleIndex={setActiveSubtitleIndex}
          drag={drag}
          setDrag={setDrag}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

function SubtitleTimelineContent({
  durationSec,
  subtitles,
  currentTime,
  onSeek,
  onUpdateSubtitle,
  onUpdateMany,
  activeSubtitleIndex,
  setActiveSubtitleIndex,
  drag,
  setDrag,
  readOnly,
}) {
  const safeDur = durationSec > 0 ? durationSec : 1;
  const containerRef = useRef(null);

  const segments = useMemo(() => {
    const ordered = (subtitles || [])
      .map((s, i) => ({ ...s, _origIndex: i }))
      .sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0));

    return ordered.map((s, idx) => {
      const startSec = clamp((s.start_ms || 0) / 1000, 0, safeDur);
      const endSec = clamp((s.end_ms || 0) / 1000, startSec, safeDur);

      const active =
        (currentTime > startSec - EPS && currentTime < endSec - 1e-6) ||
        (idx === ordered.length - 1 && currentTime >= endSec - EPS);

      return {
        idx,
        _origIndex: s._origIndex,
        startSec,
        endSec,
        text: s.text || '',
        active,
        color: colorFor(idx),
      };
    });
  }, [subtitles, currentTime, safeDur]);

  const getPreviewStartEnd = (seg) => {
    if (!drag) return { start: seg?.startSec, end: seg?.endSec };

    if (seg?.idx === drag.idx) {
      return {
        start: drag.previewStart ?? seg?.startSec,
        end: drag.previewEnd ?? seg?.endSec,
      };
    }

    if (drag.mode === 'left' && seg?.idx === drag.idx - 1) {
      const boundary = drag.previewStart ?? drag.startSec;

      return {
        start: seg.startSec,
        end: Math.max(seg.startSec + MIN_SPAN, Math.min(boundary, seg.endSec)),
      };
    }

    if (drag.mode === 'right' && seg?.idx === drag.idx + 1) {
      const boundary = drag.previewEnd ?? drag.endSec;

      return {
        start: Math.min(
          seg.endSec - MIN_SPAN,
          Math.max(boundary, seg.startSec)
        ),
        end: seg.endSec,
      };
    }

    return { start: seg?.startSec, end: seg?.endSec };
  };

  const boundsForMove = (idx, span) => {
    const prevEnd = idx > 0 ? segments[idx - 1].endSec : 0;
    const nextStart =
      idx < segments.length - 1 ? segments[idx + 1].startSec : safeDur;

    return {
      left: clamp(prevEnd + 1e-6, 0, Math.max(0, safeDur - span)),
      right: clamp(nextStart - span - 1e-6, 0, Math.max(0, safeDur - span)),
    };
  };

  const boundsForLeftEdge = (idx) => {
    const cur = segments[idx];
    const prev = segments[idx - 1];

    const min = idx === 0 ? 0 : prev.startSec + MIN_SPAN;
    const max = cur.endSec - MIN_SPAN;

    return {
      min: clamp(min, 0, safeDur),
      max: clamp(max, 0, safeDur),
    };
  };

  const boundsForRightEdge = (idx) => {
    const cur = segments[idx];
    const next = segments[idx + 1];

    const min = cur.startSec + MIN_SPAN;
    const max = idx === segments.length - 1 ? safeDur : next.endSec - MIN_SPAN;

    return {
      min: clamp(min, 0, safeDur),
      max: clamp(max, 0, safeDur),
    };
  };

  const selectSegment = (seg, start, end) => {
    setActiveSubtitleIndex?.(seg.idx + 1);
    onSeek?.(safeInside(start, end));
  };

  const onPointerDown = (e, mode, seg) => {
    if (readOnly) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);

    setDrag({
      mode,
      idx: seg.idx,
      _origIndex: seg._origIndex,
      startSec: seg.startSec,
      endSec: seg.endSec,
      secPerPx: safeDur / rect.width,
      x0: e.clientX,
      y0: e.clientY,
      isDragging: false,
    });
  };

  const onPointerMove = (e) => {
    if (!drag) return;

    const dxPx = e.clientX - drag.x0;
    const dyPx = e.clientY - drag.y0;
    const moved = Math.hypot(dxPx, dyPx) > CLICK_TOL;
    const dxSec = dxPx * drag.secPerPx;

    let start = drag.startSec;
    let end = drag.endSec;

    if (drag.mode === 'move') {
      const span = Math.max(MIN_SPAN, end - start);
      const { left, right } = boundsForMove(drag.idx, span);
      const ns = clamp(start + dxSec, left, right);

      start = ns;
      end = ns + span;
    } else if (drag.mode === 'left') {
      const { min, max } = boundsForLeftEdge(drag.idx);
      start = clamp(drag.startSec + dxSec, min, max);
    } else if (drag.mode === 'right') {
      const { min, max } = boundsForRightEdge(drag.idx);
      end = clamp(drag.endSec + dxSec, min, max);
    }

    const snap = (x) => Math.round(x / SNAP) * SNAP;

    setDrag({
      ...drag,
      isDragging: moved,
      previewStart: snap(start),
      previewEnd: snap(end),
    });
  };

  const commitUpdates = (updates) => {
    if (onUpdateMany && Array.isArray(updates) && updates.length > 1) {
      onUpdateMany(updates);
      return;
    }

    updates.forEach(({ _origIndex, patch }) => {
      onUpdateSubtitle?.(_origIndex, patch.start_ms, patch.end_ms);
    });
  };

  const onPointerUp = () => {
    if (!drag) return;

    const start = drag.previewStart ?? drag.startSec;
    const end = drag.previewEnd ?? drag.endSec;

    if (drag.mode === 'move') {
      commitUpdates([
        {
          _origIndex: drag._origIndex,
          patch: {
            start_ms: Math.round(start * 1000),
            end_ms: Math.round(end * 1000),
          },
        },
      ]);

      setDrag(null);
      return;
    }

    if (drag.mode === 'left') {
      const updates = [
        {
          _origIndex: segments[drag.idx]._origIndex,
          patch: {
            start_ms: Math.round(start * 1000),
            end_ms: Math.round(segments[drag.idx].endSec * 1000),
          },
        },
      ];

      if (drag.idx > 0) {
        const prevSeg = segments[drag.idx - 1];

        if (start < prevSeg.endSec - MIN_SPAN) {
          updates.push({
            _origIndex: prevSeg._origIndex,
            patch: {
              start_ms: Math.round(prevSeg.startSec * 1000),
              end_ms: Math.round(start * 1000),
            },
          });
        }
      }

      commitUpdates(updates);
      setDrag(null);
      return;
    }

    if (drag.mode === 'right') {
      const updates = [
        {
          _origIndex: segments[drag.idx]._origIndex,
          patch: {
            start_ms: Math.round(segments[drag.idx].startSec * 1000),
            end_ms: Math.round(end * 1000),
          },
        },
      ];

      if (drag.idx < segments.length - 1) {
        const nextSeg = segments[drag.idx + 1];

        if (end > nextSeg.startSec + MIN_SPAN) {
          updates.push({
            _origIndex: nextSeg._origIndex,
            patch: {
              start_ms: Math.round(end * 1000),
              end_ms: Math.round(nextSeg.endSec * 1000),
            },
          });
        }
      }

      commitUpdates(updates);
      setDrag(null);
    }
  };

  useEffect(() => {
    if (!drag) return undefined;

    const move = (e) => onPointerMove(e);
    const up = () => onPointerUp();

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);

    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.userSelect = previousUserSelect;
    };
  }, [drag]);

  const onBarClick = (e) => {
    if (drag) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const px = clamp(e.clientX - rect.left, 0, rect.width);
    const sec = (px / rect.width) * safeDur;

    onSeek?.(sec);
  };

  const ticks = useMemo(() => {
    if (!Number.isFinite(safeDur) || safeDur <= 0) return [0];

    const rawStep = safeDur / MAX_TICKS;
    const pow10 = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const candidates = [1, 2, 5].map((m) => m * pow10);

    let interval = candidates[candidates.length - 1];

    for (const step of candidates) {
      if (rawStep <= step) {
        interval = step;
        break;
      }
    }

    const arr = [];

    for (let t = 0; t <= safeDur + 1e-6; t += interval) {
      arr.push(clamp(t, 0, safeDur));
    }

    if (arr[arr.length - 1] !== safeDur) {
      arr.push(safeDur);
    }

    return Array.from(new Set(arr.map((x) => +x.toFixed(3))));
  }, [safeDur]);

  let dragTooltip = null;

  if (drag && drag.isDragging) {
    const seg = segments[drag.idx];
    const pStart = drag.previewStart ?? seg.startSec;
    const pEnd = drag.previewEnd ?? seg.endSec;

    let t = null;

    if (drag.mode === 'move' || drag.mode === 'left') t = pStart;
    if (drag.mode === 'right') t = pEnd;

    if (t != null) {
      const leftPct = (t / safeDur) * 100;

      dragTooltip = (
        <div
          className="pointer-events-none absolute -top-5 select-none rounded-sm bg-black/80 px-1.5 py-[1px] text-[11px] font-semibold text-white"
          style={{
            left: `calc(${leftPct}% - 20px)`,
            minWidth: '40px',
            textAlign: 'center',
          }}
        >
          {fmt(t)}
        </div>
      );
    }
  }

  return (
    <div
      data-subtitle-timeline-container
      style={{
        position: 'relative',
        width: `${Math.max(1, segments.length) * 260}px`,
        minWidth: '100%',
      }}
    >
      <div className="flex justify-between px-2 text-xs text-grey-05">
        {ticks.map((t, i) => (
          <React.Fragment key={i}>
            {i !== 0 && <span>•</span>}
            {i !== 0 && <span>•</span>}
            <span>{fmt(t)}</span>
          </React.Fragment>
        ))}
      </div>

      <div
        ref={containerRef}
        className="relative mt-2 h-[42px] w-full cursor-pointer select-none bg-grey-02 outline-none focus:outline-none"
        style={{ caretColor: 'transparent', userSelect: 'none' }}
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onBarClick}
      >
        {segments.map((seg) => {
          const p = getPreviewStartEnd(seg);
          const leftPct = (p.start / safeDur) * 100;
          const widthPct = ((p.end - p.start) / safeDur) * 100;

          const isSelected =
            activeSubtitleIndex === seg.idx + 1 ||
            seg.active ||
            (drag && drag.idx === seg.idx);

          return (
            <div
              key={seg.idx}
              className="absolute top-0 h-[42px] outline-none transition focus:outline-none"
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                zIndex: 10,
                caretColor: 'transparent',
                userSelect: 'none',
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                selectSegment(seg, p.start, p.end);
              }}
              title={seg.text}
            >
              <div
                className="rounded-[4px] border-y border-x-8 border-grey-01"
                style={{
                  position: 'absolute',
                  top: SELECT_PAD,
                  bottom: SELECT_PAD,
                  left: GAP_PX / 2 + SELECT_PAD,
                  right: GAP_PX / 2 + SELECT_PAD,
                  backgroundColor: seg.color,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  ...(isSelected ? { borderColor: '#4D4D50' } : null),
                }}
              />

              <span className="absolute left-[5px] top-[11px] h-5 w-[2px] rounded-lg bg-grey-02" />
              <span className="absolute right-[5px] top-[11px] h-5 w-[2px] rounded-lg bg-grey-02" />

              <div
                className="absolute max-w-[calc(100%_-_22px)] text-[11px] font-medium"
                style={{
                  top: SELECT_PAD,
                  bottom: SELECT_PAD,
                  left: GAP_PX / 2 + SELECT_PAD,
                  right: GAP_PX / 2 + SELECT_PAD,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 4px',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  color: '#FFFFFF',
                  zIndex: 11,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <span className="pl-3 text-xs font-semibold">
                  {seg.idx + 1}
                </span>

                <div className="ml-4 mr-3 max-w-[100%] overflow-hidden text-ellipsis text-xs text-white opacity-80">
                  {abbr(seg.text.replace(/\s+/g, ' ').trim() || 'Subtitle', 28)}
                </div>
              </div>

              {!readOnly && (
                <>
                  <div
                    className="absolute inset-0 cursor-grab active:cursor-grabbing"
                    style={{ userSelect: 'none', caretColor: 'transparent' }}
                    onPointerDown={(e) => onPointerDown(e, 'move', seg)}
                  />

                  <div
                    className="absolute left-0 top-0 h-full cursor-ew-resize"
                    style={{
                      width: HANDLE_W,
                      userSelect: 'none',
                      caretColor: 'transparent',
                    }}
                    onPointerDown={(e) => onPointerDown(e, 'left', seg)}
                    title={
                      seg.idx === 0 ? 'Adjust start' : 'Adjust shared boundary'
                    }
                  />

                  <div
                    className="absolute right-0 top-0 h-full cursor-ew-resize"
                    style={{
                      width: HANDLE_W,
                      userSelect: 'none',
                      caretColor: 'transparent',
                    }}
                    onPointerDown={(e) => onPointerDown(e, 'right', seg)}
                    title={
                      seg.idx === segments.length - 1
                        ? 'Adjust end'
                        : 'Adjust shared boundary'
                    }
                  />
                </>
              )}
            </div>
          );
        })}

        {dragTooltip}

        <div
          className="absolute top-[-38px] z-[10] h-[123px] w-[2px] bg-primary-04"
          style={{
            left: `${(clamp(currentTime, 0, safeDur) / safeDur) * 100}%`,
          }}
        >
          <span
            className="absolute -left-1"
            style={{
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '10px 5px 0 5px',
              borderColor: '#FDCA33 transparent transparent transparent',
            }}
          />

          <span
            className="absolute -left-1"
            style={{
              bottom: 0,
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '0 5px 10px 5px',
              borderColor: 'transparent transparent #FDCA33 transparent',
            }}
          />

          <span className="absolute -left-[3px] top-[43px] h-8 w-2 rounded-lg bg-primary-04" />
        </div>
      </div>
    </div>
  );
}
